import React, { useCallback, useState } from 'react';
import { parse, stringify } from 'query-string';
import DialogTemplate from '../../dialogs_common/DialogTemplate';
import CloseDialogButton from '../../dialogs_common/CloseDialogButton';
import TagTypes from '../../constants/TagTypes';
import SearchFilter from '../../forms/_fields_v2/SearchFilter';
import DisclosureTagFilter from '../../forms/_fields_v2/DisclosureTagFilter';
// import SearchTags from '../../components/SearchTags';
import ClearAllFilters from '../../components/ClearAllFilters';
import { getSearchUtils } from '../../utils/query-utils';
import useQueryBase from '../../pages/evaluations-project-partners/useQuery';
import useQueryContributions from '../../pages/evaluations-project-partners-filtered/contributions/useQuery';
import useQueryTestimonials from '../../pages/evaluations-project-partners-filtered/testimonials/useQuery';
import useFilterCount from '../../hooks/useFilterCount';
import useStewardshipFilter from './useStewardshipFilter';
// import REQUIRED_FIELDS_PUBLIC from '../../pages/partners/REQUIRED_FIELDS_PUBLIC';
import { REQUIRED_PARTNER_FIELDS } from '../../utils/user/REQUIRED_FIELDS';
import { SolidButton } from '../../@communityshare/ui/Button';
import DisclosureFilter from '../../forms/_fields_v2/DisclosureFilter';
import { attributeKeys } from '../../pages/partners/queryUtils';
import _ from 'lodash';
import DisclosureBaseFilter from '../../forms/_fields_v2/DisclosureBaseFilter';
import PayloadStates from '../../constants/PayloadStates';
import moment from 'moment-timezone';
import useNetworkFilter from './useNetworkFilter';
import SolidButtonLoader from '../../@communityshare/ui/Button/SolidButtonLoader';
import { tagNodes_tagId } from '../../utils/query-helpers';

export default function PartnerFiltersDialog(props) {
  const { router, schoolStart, schoolEnd, variant = 'base' } = props;

  const useQuery = _.defaultTo({
    'base': useQueryBase,
    'contributions': useQueryContributions,
    'testimonials': useQueryTestimonials
  }[variant], useQueryBase);

  const extra = _.defaultTo({
    'contributions': {
      completed: true,
      contributed: 'Yes'
    },
    'testimonials': {
      completed: true,
      recommend: 'Yes',
      testimonial: {
        $not: ''
      }
    }
  }[variant], {});

  const { location, history } = router;

  const [search, setSearch] = useState(parse(location.search));
  const [querySearch, setQuerySearch] = useState(parse(location.search));

  const debounceSetQuerySearch = useCallback(_.debounce(setQuerySearch, 250), []);

  const networkFilter = useNetworkFilter(search);
  // const stewardshipFilter = useStewardshipFilter(search);
  // const interactionTypesFilter = useInteractionTypesFilterParam(search);
  // const distanceFilter = useDistanceFilterParam(search);

  const filterFields = {
    title: 'title',
    partner: 'partner',
    educator: 'educator',
    school: 'school',
    district: 'district',

    statuses: 'statuses',
    recommendPlatforms: 'recommendPlatforms',

    offerStatements: 'offerStatements',
    learnStatements: 'learnStatements',
    understandingStatements: 'understandingStatements',
    connectionStatements: 'connectionStatements',
    volunteerStatements: 'volunteerStatements',

    activities: 'activities',
    ages: 'ages',
    commitments: 'commitments',
    expertiseTypes: 'expertiseTypes',
    groups: 'groups',
    guidances: 'guidances',
    interactionTypes: 'interactionTypes',
    subjects: 'subjects'
  };

  const filterCount = useFilterCount(search, _.keys(filterFields));

  function onSubmit() {
    history.push({
      pathname: location.pathname,
      search: stringify({
        ...search,
        page: 1
      })
    });
    props.onClose();
  }

  function onChange(nextSearch) {
    setSearch(nextSearch);
    debounceSetQuerySearch(nextSearch);
  }

  const searchUtils = getSearchUtils(search, onChange);

  const result = useQuery({
    search: querySearch,
    schoolStart,
    schoolEnd
  });

  function getProjectTagParams(tag) {
    return {
      where: {
        eager: {
          $where: {
            ...tagNodes_tagId(tag),
            ...extra,
            ...networkFilter,
            createdAt: {
              $lte: schoolEnd,
              $gte: schoolStart
            }
          },
        }
      },
      pagination: {
        pageSize: 1
      }
    };
  }

  function getProjectActivityParams(tag) {
    return {
      where: {
        eager: {
          $where: {
            project: {
              $where: {
                activityId: tag.id
              }
            },
            ...extra,
            ...networkFilter,
            createdAt: {
              $lte: schoolEnd,
              $gte: schoolStart
            }
          },
        }
      },
      pagination: {
        pageSize: 1
      }
    };
  }

  function getStatementParams(param) {
    return function(model) {
      return {
        where: {
          eager: {
            $where: {
              [param]: model.data.name === 'Unanswered' ? {
                $null: true
              } : {
                $equals: model.data.name
              },
              ...extra,
              ...networkFilter,
              createdAt: {
                $lte: schoolEnd,
                $gte: schoolStart
              }
            }
          }
        },
        pagination: {
          pageSize: 1
        }
      };
    };
  }

  const statementTags = {
    state: PayloadStates.RESOLVED,
    data: [
      'Strongly Agree',
      'Agree',
      'Neutral',
      'Disagree',
      'Unanswered'
    ].map(function(name, index) {
      return {
        id: index + 1,
        data: {
          name: name
        }
      };
    })
  };

  return (
    <DialogTemplate className="w-screen max-w-screen-sm h-[calc(100dvh-32px)] max-h-[calc(720px)]">
      <CloseDialogButton onClick={props.onClose} />
      <div className="flex flex-col space-y-8x h-full">
        <div className="space-y-1 p-6 border-b border-cs-gray-200">
          <h2 className="text-cs-gray-900 text-2xl font-semibold">
            Filters
          </h2>
          <div className="text-cs-gray-500 text-sm">
            Narrow your search results.
          </div>
          {/*<div className="pt-4">*/}
          {/*  <SearchTags*/}
          {/*    search={search}*/}
          {/*    onChange={setSearch}*/}
          {/*  />*/}
          {/*</div>*/}
        </div>
        <div className="px-6 py-8 space-y-8 overflow-y-auto h-full">
          <div className="space-y-4">
            <DisclosureFilter
              label="Search"
              value={[
                searchUtils(filterFields.title).value,
                searchUtils(filterFields.partner).value,
                searchUtils(filterFields.educator).value,
                searchUtils(filterFields.school).value,
                searchUtils(filterFields.district).value
              ]}
            >
              <SearchFilter
                label="Project title"
                description="Find evaluations for a project title that matches any of the words you enter."
                value={searchUtils(filterFields.title).value}
                onChange={searchUtils(filterFields.title).onChangeValue}
              />
              <SearchFilter
                label="Partner name"
                description="Find evauations for a partner whose first or last name matches any of the words you enter."
                value={searchUtils(filterFields.partner).value}
                onChange={searchUtils(filterFields.partner).onChangeValue}
              />
              <SearchFilter
                label="Educator name"
                description="Find evauations for a project created by an educator whose first or last name matches any of the words you enter."
                value={searchUtils(filterFields.educator).value}
                onChange={searchUtils(filterFields.educator).onChangeValue}
              />
              <SearchFilter
                label="School"
                description="Find evauations for a project affiliated with a school whose name contains the phrase you type."
                value={searchUtils(filterFields.school).value}
                onChange={searchUtils(filterFields.school).onChangeValue}
              />
              <SearchFilter
                label="District"
                description="Find evauations for a project affiliated with a school district whose name contains the phrase you type."
                value={searchUtils(filterFields.district).value}
                onChange={searchUtils(filterFields.district).onChangeValue}
              />
            </DisclosureFilter>
          </div>
          {variant === 'base' && (
            <>
              <DisclosureBaseFilter
                modelName="evaluationProjectPartner"
                label="Status"
                getParams={function getTestimonialParams(model) {
                  return {
                    where: {
                      eager: {
                        $where: {
                          completed: model.data.name === 'Complete' ? true : false,
                          ...extra,
                          ...networkFilter,
                          createdAt: {
                            $lte: schoolEnd,
                            $gte: schoolStart
                          }
                        }
                      }
                    },
                    pagination: {
                      pageSize: 1
                    }
                  };
                }}
                value={searchUtils(filterFields.statuses).value}
                onChange={searchUtils(filterFields.statuses).onChangeArray}
                tags={{
                  state: PayloadStates.RESOLVED,
                  data: [
                    {
                      id: 1,
                      data: {
                        name: 'Complete'
                      }
                    },
                    {
                      id: 2,
                      data: {
                        name: 'Pending'
                      }
                    }
                  ]
                }}
              />
              <DisclosureBaseFilter
                modelName="evaluationProjectPartner"
                label="Recommends platform"
                getParams={function getTestimonialParams(model) {
                  const valueMap = {
                    1: 'Yes',
                    2: 'No',
                    3: {
                      $null: true
                    }
                  };

                  return {
                    where: {
                      eager: {
                        $where: {
                          recommend: valueMap[model.id],
                          ...extra,
                          ...networkFilter,
                          createdAt: {
                            $lte: schoolEnd,
                            $gte: schoolStart
                          }
                        }
                      }
                    },
                    pagination: {
                      pageSize: 1
                    }
                  };
                }}
                value={searchUtils(filterFields.recommendPlatforms).value}
                onChange={searchUtils(filterFields.recommendPlatforms).onChangeArray}
                tags={{
                  state: PayloadStates.RESOLVED,
                  data: [
                    {
                      id: 1,
                      data: {
                        name: 'Yes'
                      }
                    },
                    {
                      id: 2,
                      data: {
                        name: 'No'
                      }
                    },
                    {
                      id: 3,
                      data: {
                        name: 'Not answered'
                      }
                    }
                  ]
                }}
              />
            </>
          )}
          <DisclosureBaseFilter
            modelName="evaluationProjectPartner"
            label="It helped me realize that I have something valuable to offer to students and teachers."
            getParams={getStatementParams('offerStatement')}
            value={searchUtils(filterFields.offerStatements).value}
            onChange={searchUtils(filterFields.offerStatements).onChangeArray}
            tags={statementTags}
          />
          <DisclosureBaseFilter
            modelName="evaluationProjectPartner"
            label="It helped me realize that I have things to learn from others who may be different than me."
            getParams={getStatementParams('learnStatement')}
            value={searchUtils(filterFields.learnStatements).value}
            onChange={searchUtils(filterFields.learnStatements).onChangeArray}
            tags={statementTags}
          />
          <DisclosureBaseFilter
            modelName="evaluationProjectPartner"
            label="It increased my understanding of the realities of a teacher's job."
            getParams={getStatementParams('understandingStatement')}
            value={searchUtils(filterFields.understandingStatements).value}
            onChange={searchUtils(filterFields.understandingStatements).onChangeArray}
            tags={statementTags}
          />
          <DisclosureBaseFilter
            modelName="evaluationProjectPartner"
            label="I feel a greater sense of connection to and belonging with my community."
            getParams={getStatementParams('connectionStatement')}
            value={searchUtils(filterFields.connectionStatements).value}
            onChange={searchUtils(filterFields.connectionStatements).onChangeArray}
            tags={statementTags}
          />
          <DisclosureBaseFilter
            modelName="evaluationProjectPartner"
            label="I am likely to volunteer again."
            getParams={getStatementParams('volunteerStatement')}
            value={searchUtils(filterFields.volunteerStatements).value}
            onChange={searchUtils(filterFields.volunteerStatements).onChangeArray}
            tags={statementTags}
          />
          <DisclosureTagFilter
            modelName="evaluationProjectPartner"
            type={TagTypes.Activity}
            label="Activity"
            getParams={getProjectActivityParams}
            value={searchUtils(filterFields.activities).value}
            onChange={searchUtils(filterFields.activities).onChangeArray}
          />
          <DisclosureTagFilter
            modelName="evaluationProjectPartner"
            type={TagTypes.ExpertiseType}
            label="Skills and experience"
            getParams={getProjectTagParams}
            value={searchUtils(filterFields.expertiseTypes).value}
            onChange={searchUtils(filterFields.expertiseTypes).onChangeArray}
          />
          <DisclosureTagFilter
            modelName="evaluationProjectPartner"
            type={TagTypes.Subject}
            label="Subject areas"
            getParams={getProjectTagParams}
            value={searchUtils(filterFields.subjects).value}
            onChange={searchUtils(filterFields.subjects).onChangeArray}
          />
          <DisclosureTagFilter
            modelName="evaluationProjectPartner"
            type={TagTypes.Age}
            label="Student ages"
            getParams={getProjectTagParams}
            value={searchUtils(filterFields.ages).value}
            onChange={searchUtils(filterFields.ages).onChangeArray}
          />
          {/*<DisclosureTagFilter*/}
          {/*  modelName="evaluationProjectPartner"*/}
          {/*  type={TagTypes.Day}*/}
          {/*  label="Day"*/}
          {/*  getParams={getProjectTagParams}*/}
          {/*  value={searchUtils(filterFields.days).value}*/}
          {/*  onChange={searchUtils(filterFields.days).onChangeArray}*/}
          {/*/>*/}
          <DisclosureTagFilter
            modelName="evaluationProjectPartner"
            type={TagTypes.InteractionType}
            label="Interaction types"
            getParams={getProjectTagParams}
            value={searchUtils(filterFields.interactionTypes).value}
            onChange={searchUtils(filterFields.interactionTypes).onChangeArray}
          />
          {/*<DisclosureTagFilter*/}
          {/*  modelName="evaluationProjectPartner"*/}
          {/*  type={TagTypes.Participant}*/}
          {/*  label="Participants"*/}
          {/*  getParams={getProjectTagParams}*/}
          {/*  value={searchUtils(filterFields.participants).value}*/}
          {/*  onChange={searchUtils(filterFields.participants).onChangeArray}*/}
          {/*/>*/}
          {/*<DisclosureTagFilter*/}
          {/*  modelName="evaluationProjectPartner"*/}
          {/*  type={TagTypes.Time}*/}
          {/*  label="Time"*/}
          {/*  getParams={getProjectTagParams}*/}
          {/*  value={searchUtils(filterFields.times).value}*/}
          {/*  onChange={searchUtils(filterFields.times).onChangeArray}*/}
          {/*/>*/}
          <DisclosureTagFilter
            modelName="evaluationProjectPartner"
            type={TagTypes.Commitment}
            label="Commitment"
            getParams={getProjectTagParams}
            value={searchUtils(filterFields.commitments).value}
            onChange={searchUtils(filterFields.commitments).onChangeArray}
          />
        </div>
        <div className="p-6 flex items-center justify-between border-t border-cs-gray-200">
          <ClearAllFilters
            show={true}
            className="hover:opacity-70"
            onClick={() => setSearch(_.omit(search, _.keys(filterFields)))}
            count={filterCount}
          />
          {result._isPreviousData ? (
            <SolidButton onClick={onSubmit}>
              <SolidButtonLoader />
            </SolidButton>
          ) : (
            <SolidButton onClick={onSubmit}>
              Show {result.meta?.totalCount} results
            </SolidButton>
          )}
        </div>
      </div>
    </DialogTemplate>
  );
};
